import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, MapPin, Phone, Globe, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PORTAL_URL } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "h-11 bg-white border-black/10 text-[#1F1F1F] placeholder:text-[#9ca3af] focus-visible:ring-[#5A189A]";
const labelCls = "mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#4b5563]";

const CONTACT_INFO = [
  { icon: Mail, label: "Email", value: "xpressngoship@gmail.com", href: "mailto:xpressngoship@gmail.com" },
  { icon: Phone, label: "Call / WhatsApp", value: "876 825 6468 · 876 538 6101 · 876 826 1828", href: "tel:+18765386101" },
  { icon: Globe, label: "Website", value: "www.xpressngoship.com", href: "https://www.xpressngoship.com" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { summary, service } = e.detail || {};
      setForm((f) => ({ ...f, service: service || f.service, message: summary || f.message }));
      toast.success("Estimate added — just add your details and send.");
    };
    window.addEventListener("prefill-quote", handler);
    return () => window.removeEventListener("prefill-quote", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — our team will reply within one business hour.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#F5F5F7] border-t border-black/5">
      <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#E31B23]">
              <span className="h-px w-8 bg-[#E31B23]" /> Let's talk
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-tight text-[#1F1F1F] sm:text-5xl">
              Ready to move something big?
            </h2>
            <p className="mt-4 max-w-md text-[#4b5563]">
              Tell us what you ship and where it needs to go. We'll design a network around it.
            </p>

            <div className="mt-10 space-y-4">
              {CONTACT_INFO.map((c) => (
                <a key={c.label} href={c.href} data-testid={`contact-info-${c.label.toLowerCase()}`}
                  className="flex items-center gap-4 rounded-2xl border border-black/5 bg-white p-4 hover:border-[#5A189A]/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#5A189A]/10">
                    <c.icon size={18} className="text-[#5A189A]" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#9ca3af]">{c.label}</div>
                    <div className="text-[#1F1F1F] font-medium">{c.value}</div>
                  </div>
                </a>
              ))}
            </div>

            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex text-sm font-medium text-[#5A189A] hover:text-[#3D0A6E]">
              Existing customer? Open the client portal →
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="card-soft rounded-3xl p-6 sm:p-8" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Name *</label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Jane Doe" />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="jane@company.com" />
                </div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Company</label>
                  <Input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} placeholder="Acme Inc." />
                </div>
                <div>
                  <label className={labelCls}>Service interest</label>
                  <Input data-testid="contact-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={inputCls} placeholder="Air freight, 3PL…" />
                </div>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Message *</label>
                <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-white border-black/10 text-[#1F1F1F] placeholder:text-[#9ca3af] focus-visible:ring-[#5A189A] resize-none" placeholder="Tell us about your shipment…" />
              </div>
              <button type="submit" data-testid="contact-submit" disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60 shadow-[0_12px_32px_-12px_rgba(227,27,35,0.6)]">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send message
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
